import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import toast from "react-hot-toast";
import { type Styles, css } from "../../../../styled-system/css";
import Header from "../../../components/header";
import {
  KYOTO_REPRESENTATIVE_CAMPAIGN_REWARD,
  KYOTO_REPRESENTATIVE_LIMIT,
} from "../../../config";
import { useFooter } from "../../../contexts/footer-context";
import {
  type RepresentativeCampaignReceiveMethod,
  canShowKyotoRepresentativeCampaign,
  clearRepresentedSubscriptionIdJwtForRepresentativeCampaign,
  getRepresentativeCampaignState,
  getRepresentedSubscriptionIdJwtForRepresentativeCampaign,
} from "../../../features/representative-campaign";
import { getRepresentativeCampaignApplyErrorMessage } from "../../../features/representative-campaign-error-message";
import { getCurrentTime } from "../../../libs/date";
import { t } from "../../../libs/i18n";
import { setPendingModal } from "../../../libs/pending-modal";
import { getCityAddressWithCache } from "../../../libs/sdk";
import { getAppEnv } from "../../../libs/utils";

type ProcessingSearch = {
  method: RepresentativeCampaignReceiveMethod;
};

export const Route = createFileRoute(
  "/_authed/kyoto-representative-campaign/processing",
)({
  validateSearch: (search: Record<string, unknown>): ProcessingSearch => {
    return {
      method: search.method === "family" ? "family" : "self",
    };
  },
  beforeLoad: async ({ context: { tenant } }) => {
    const cityAddress = (await getCityAddressWithCache()) ?? "";
    const currentTime = await getCurrentTime();
    if (
      !canShowKyotoRepresentativeCampaign({
        tenantSlug: tenant.slug,
        cityAddress,
        currentTime: currentTime.date,
        appEnv: getAppEnv(),
      })
    ) {
      throw redirect({ to: "/" });
    }
  },
  loader: async ({ context: { client }, location }) => {
    const { method } = location.search as ProcessingSearch;
    const { represent, represented } = await client.getRepresentations({});
    const state = getRepresentativeCampaignState({ represent, represented });

    if (method === "self" && state.selfApplied) {
      throw redirect({ to: "/kyoto-representative-campaign" });
    }
    if (
      method === "family" &&
      state.representedCount >= KYOTO_REPRESENTATIVE_LIMIT
    ) {
      throw redirect({ to: "/kyoto-representative-campaign" });
    }
  },
  component: KyotoRepresentativeCampaignProcessingPage,
});

const loading: Styles = {
  display: "inline-block",
  width: "100%",
  height: "100%",
  background: "linear-gradient(110deg, #ececec 8%, #f5f5f5 18%, #ececec 33%)",
  backgroundSize: "200% 100%",
  animation: "1.2s shine linear infinite",
};

const dot: Styles = {
  w: "20px",
  h: "20px",
  bg: "#2563EB",
  rounded: "full",
  animation: "bounce 1.2s infinite ease-in-out",
};

function KyotoRepresentativeCampaignProcessingPage() {
  const { method } = Route.useSearch();
  const { client } = Route.useRouteContext();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setDisabled: setFooterDisabled } = useFooter();

  useEffect(() => {
    setFooterDisabled(true);

    return () => {
      setFooterDisabled(false);
    };
  }, [setFooterDisabled]);

  useEffect(() => {
    let canceled = false;

    const timeout = setTimeout(() => {
      void (async () => {
        try {
          if (method === "self") {
            await client.applySelfRepresentativeSignupCampaign({});
          } else {
            const signedRepresentedSubscriptionIdJwt =
              getRepresentedSubscriptionIdJwtForRepresentativeCampaign();
            if (!signedRepresentedSubscriptionIdJwt) {
              throw new Error(
                t("家族等のマイナンバーカードの読み取り情報が見つかりません"),
              );
            }
            await client.applyRepresentativeSignupCampaign({
              signedRepresentedSubscriptionIdJwt,
            });
          }

          clearRepresentedSubscriptionIdJwtForRepresentativeCampaign();
          queryClient.resetQueries({ queryKey: ["transactions"] });
          setPendingModal({
            type: "kyoto-representative-campaign-completed",
            data: { amount: KYOTO_REPRESENTATIVE_CAMPAIGN_REWARD },
          });

          if (!canceled) {
            await navigate({ to: "/", replace: true });
          }
        } catch (error) {
          clearRepresentedSubscriptionIdJwtForRepresentativeCampaign();
          toast.error(getRepresentativeCampaignApplyErrorMessage(error));
          if (!canceled) {
            await navigate({
              to: "/kyoto-representative-campaign",
              replace: true,
            });
          }
        }
      })();
    }, 2000);

    return () => {
      canceled = true;
      clearTimeout(timeout);
    };
  }, [client, method, navigate, queryClient]);

  return (
    <>
      <Header title={t("ポイント受け取り")} />
      <div
        className={css({
          p: "20px",
        })}
      >
        <section
          className={css({
            bg: "white",
            pb: "28px",
            rounded: "2xl",
          })}
        >
          <div
            className={css({
              bg: "surface.disable",
              height: "112px",
              roundedTop: "2xl",
            })}
          >
            <div className={css({ ...loading, roundedTop: "2xl" })} />
          </div>
          <div
            className={css({
              display: "flex",
              flexDir: "column",
              gap: "24px",
              px: "38px",
            })}
          >
            <div
              className={css({
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: "10px",
                mt: "24px",
              })}
            >
              <div className={css({ ...dot, animationDelay: "0s" })} />
              <div className={css({ ...dot, animationDelay: "0.2s" })} />
              <div className={css({ ...dot, animationDelay: "0.4s" })} />
              <div className={css({ ...dot, animationDelay: "0.6s" })} />
            </div>
            <h1
              className={css({
                textAlign: "center",
              })}
            >
              {t("応募中...")}
            </h1>
            <div
              className={css({
                h: "55px",
                w: "full",
              })}
            >
              <div className={css(loading)} />
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
